import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useEffect, useState } from "react";

import {
  useCreateFolderMutation,
  useGetFoldersQuery,
} from "api/memberPortal/files/files.api";
import { UserUploadedFile } from "api/memberPortal/files/files.types";
import { useAppForm } from "components/Forms/AppForm";

import {
  CreateFolderFormValues,
  CreateFolderSchema,
} from "./CreateFolder.schema";

interface CreateFolderProps {
  show: boolean;
  handleClose: () => void;
  currentFolder?: { file_id: number };
  setUpdate: (_fileId: number) => void;
  currentPage: string;
  setAlert: (_alert: { message: string; variant: string }) => void;
}

const FOLDERS: UserUploadedFile[] = [];

const CreateFolder = ({
  show,
  handleClose,
  currentFolder,
  setUpdate,
  currentPage,
  setAlert,
}: CreateFolderProps) => {
  const [error, setError] = useState<string>("");

  const { data: folders = FOLDERS, isLoading: foldersLoading } =
    useGetFoldersQuery({
      page: currentPage,
    });

  const [createFolder, { isLoading: isCreating }] = useCreateFolderMutation();

  const folderForm = useAppForm({
    defaultValues: {
      file_name: "",
      parent: currentFolder?.file_id?.toString(),
    } as CreateFolderFormValues,

    validators: {
      onSubmit: CreateFolderSchema,
    },

    onSubmit: async ({ value }) => {
      try {
        setError("");
        const response = await createFolder({
          file_name: value.file_name,
          parent: value.parent ? Number(value.parent) : undefined,
          file_type: currentPage,
          isFolder: true,
        }).unwrap();

        setUpdate(response.file_id);
        handleClose();
        setAlert({
          message: "Folder created successfully!",
          variant: "success",
        });
        folderForm.reset();
      } catch (err) {
        setError("Folder creation failed. Please try again.");
        console.log("Folder creation failed: ", err);
      }
    },

    onSubmitInvalid: async () => {
      setError("Please fill out all required fields before submitting");
    },
  });

  useEffect(() => {
    if (currentFolder?.file_id) {
      folderForm.setFieldValue("parent", currentFolder.file_id.toString());
    }
  }, [currentFolder, folderForm]);

  const handleDialogClose = () => {
    handleClose();
    folderForm.reset();
    setError("");
  };

  return (
    <Dialog open={show} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          folderForm.handleSubmit();
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CreateNewFolderIcon />
            Create New Folder
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {error && (
              <Alert severity="error" variant="standard">
                {error}
              </Alert>
            )}

            <folderForm.AppField name="parent">
              {(field) => (
                <field.SelectField
                  label="Parent Folder"
                  options={folders.map((folder) => ({
                    value: folder.file_id.toString(),
                    label: folder.file_location,
                  }))}
                  disabled={foldersLoading}
                />
              )}
            </folderForm.AppField>

            <folderForm.AppField name="file_name">
              {(field) => <field.TextInput label="Folder Name" />}
            </folderForm.AppField>
          </Box>
        </DialogContent>

        <DialogActions sx={{ padding: 2, gap: 1 }}>
          <Button variant="contained" color="error" onClick={handleDialogClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="success"
            disabled={isCreating}
          >
            Create
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateFolder;
